import type { CSSProperties } from 'react'

interface ResolvedBreadcrumbItemProps {
  isLast: boolean
  label: string
  onNavigate: (to: string) => void
  params: Record<string, string | undefined>
  pathname: string
}

function BreadcrumbItem({
  label,
  onClick,
  muted = false,
}: {
  label: string
  onClick?: () => void
  muted?: boolean
}) {
  const style: CSSProperties = {
    fontSize: 13,
    fontWeight: onClick ? 500 : 400,
    maxWidth: 180,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: muted
      ? 'var(--text-dim, #6b7280)'
      : onClick
        ? 'var(--text-secondary, #9ca3af)'
        : 'var(--text-primary, #e4e7ec)',
    cursor: onClick ? 'pointer' : 'default',
    background: 'transparent',
    border: 'none',
    padding: 0,
    display: 'inline',
    lineHeight: 1,
  }

  if (!onClick) return <span title={label} style={style}>{label}</span>
  return <button type="button" title={label} style={style} onClick={onClick}>{label}</button>
}

function resolveBreadcrumbTarget(
  label: string,
  pathname: string,
  params: Record<string, string | undefined>,
  isLast: boolean,
) {
  if (label === 'Workspaces') {
    return '/workspaces'
  }

  const workspaceSlug = params.workspaceSlug
  if (workspaceSlug && label === workspaceSlug) {
    return `/workspace/${workspaceSlug}/projects`
  }

  if (isLast) {
    return null
  }

  return pathname
}

export function ResolvedBreadcrumbItem({
  isLast,
  label,
  onNavigate,
  params,
  pathname,
}: ResolvedBreadcrumbItemProps) {
  const navigateTo = resolveBreadcrumbTarget(label, pathname, params, isLast)

  return (
    <BreadcrumbItem
      label={label}
      muted={isLast && !navigateTo}
      onClick={navigateTo ? () => onNavigate(navigateTo) : undefined}
    />
  )
}
